// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

/**
 * One bid package, levelled.
 *
 * This exists so the printed sheet and the screen cannot disagree. A bid
 * review is read off a printout in a room full of people who will act on it,
 * and "the paper says something different from the app" is the one failure
 * that costs more than having no printout at all.
 *
 * The arithmetic is this project's, unchanged:
 *
 *     leveled total  =  the bidder's own lines
 *                    +  every adjustment they did NOT include
 *
 * An adjustment marked not-included is scope that bidder left out, carried at
 * the amount the estimator plugged, so that every column is priced for the
 * same work. That is the whole point of levelling, and it is why the lowest
 * bid and the lowest LEVELLED bid are often different firms.
 */

import type { Bid, BidPartner, BidScope, LevelingAdjustment } from "../projectData";

/** A bid's own total, from its line items. */
export const bidTotal = (bid: Bid | null | undefined): number =>
  (bid?.items || []).reduce((s, i) => s + (i.qty || 0) * (i.unitCost || 0), 0);

export interface PartnerColumn {
  partnerId: string;
  name: string;
  /** Sum of this partner's active bids. */
  base: number;
  /** Plugs for scope they did not carry. */
  adjustments: number;
  /** base + adjustments: what they are compared at. */
  leveled: number;
  /** Lowest leveled total in the package. Ties mark every firm that ties. */
  isLow: boolean;
  /** Against the lowest, in dollars. Zero for the low bidder. */
  deltaFromLow: number;
  /** Nothing priced at all. */
  noBid: boolean;
}

export interface PackageComparison {
  scope: BidScope;
  budget: number;
  partners: PartnerColumn[];
  /** Adjustment rows that apply to anyone in this package. */
  adjustments: LevelingAdjustment[];
  /** Lowest leveled total, or 0 when nobody has bid. */
  lowLeveled: number;
  /** lowLeveled − budget. Negative is under. */
  deltaVsBudget: number;
  /** How far apart the bidders are, as a share of the low bid. */
  spreadPct: number;
}

/**
 * Build one package's comparison.
 *
 * `budget` is passed rather than computed because what a scope covers is
 * decided in scopeBudget, and that decision has already caused one wrong
 * number on this screen -- a framing package compared against all of
 * Division 06. One definition, not two.
 */
export function comparePackage(
  scope: BidScope,
  allBids: Bid[],
  partners: BidPartner[],
  allAdjustments: LevelingAdjustment[],
  budget: number,
): PackageComparison {
  const scopeBids = (allBids || []).filter(b => b.scopeId === scope.id && b.status === "Active");
  const adjustments = (allAdjustments || []).filter(a => a.scopeId === scope.id);
  const partnerIds = [...new Set(scopeBids.map(b => b.partnerId))];

  const cols: PartnerColumn[] = partnerIds.map(pid => {
    const base = scopeBids.filter(b => b.partnerId === pid).reduce((s, b) => s + bidTotal(b), 0);
    const adj = adjustments.reduce((s, a) => {
      const p = a.partners?.[pid];
      return s + (p && !p.included ? (p.amount || 0) : 0);
    }, 0);
    return {
      partnerId: pid,
      name: partners.find(p => p.id === pid)?.name || "Unknown",
      base, adjustments: adj, leveled: base + adj,
      isLow: false, deltaFromLow: 0, noBid: base === 0 && adj === 0,
    };
  });

  const priced = cols.filter(c => c.leveled > 0);
  const lowLeveled = priced.length ? Math.min(...priced.map(c => c.leveled)) : 0;
  for (const c of cols) {
    c.isLow = lowLeveled > 0 && c.leveled === lowLeveled;
    c.deltaFromLow = lowLeveled > 0 && c.leveled > 0 ? c.leveled - lowLeveled : 0;
  }
  // Cheapest first: a comparison is read looking for the number to beat.
  cols.sort((a, b) => (a.leveled || Infinity) - (b.leveled || Infinity));

  const hi = priced.length ? Math.max(...priced.map(c => c.leveled)) : 0;
  return {
    scope, budget, partners: cols, adjustments,
    lowLeveled,
    deltaVsBudget: lowLeveled > 0 ? lowLeveled - budget : 0,
    spreadPct: lowLeveled > 0 ? ((hi - lowLeveled) / lowLeveled) * 100 : 0,
  };
}

/** Every line item in the package, with each bidder's number against it. */
export interface ComparisonRow {
  label: string;
  /** Index-aligned with the comparison's `partners`. */
  amounts: Array<number | null>;
  /** This row is a levelling plug rather than a bidder's own line. */
  isAdjustment: boolean;
}

/**
 * The rows of the printed sheet.
 *
 * Bid lines are matched across bidders by description, because that is the
 * only thing they share -- two firms pricing the same package give their
 * lines different ids and often different wording. Matching is exact after
 * trimming and case folding; anything else lands on its own row rather than
 * being merged into a line it might not be.
 */
export function comparisonRows(cmp: PackageComparison, allBids: Bid[]): ComparisonRow[] {
  const idx = new Map<string, number>();
  cmp.partners.forEach((p, i) => idx.set(p.partnerId, i));
  const blank = () => cmp.partners.map(() => null as number | null);

  const byLabel = new Map<string, ComparisonRow>();
  const order: string[] = [];
  const put = (label: string, i: number, amt: number, isAdjustment: boolean) => {
    const key = label.trim().toLowerCase() + (isAdjustment ? "\u0000adj" : "");
    let row = byLabel.get(key);
    if (!row) {
      row = { label: label.trim() || "(untitled)", amounts: blank(), isAdjustment };
      byLabel.set(key, row);
      order.push(key);
    }
    row.amounts[i] = (row.amounts[i] || 0) + amt;
  };

  for (const bid of allBids || []) {
    if (bid.scopeId !== cmp.scope.id || bid.status !== "Active") continue;
    const i = idx.get(bid.partnerId);
    if (i == null) continue;
    for (const item of bid.items || []) {
      put(item.description || "", i, (item.qty || 0) * (item.unitCost || 0), false);
    }
  }
  for (const a of cmp.adjustments) {
    for (const [pid, p] of Object.entries(a.partners || {})) {
      const i = idx.get(pid);
      if (i == null || !p || p.included) continue;
      put(a.description || "", i, p.amount || 0, true);
    }
  }
  // Bid lines first, plugs after: the plugs are what was added to make the
  // columns comparable, and they read as a block.
  return order.map(k => byLabel.get(k)!).sort((a, b) => Number(a.isAdjustment) - Number(b.isAdjustment));
}
